import { useState, useMemo, useEffect } from "react";
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { Link } from 'react-router-dom';

import { updateRoomAccess, getRoomsByProjectId, getFloorsBySiteId } from "../../api/locations";
import { CreateRoomModal } from "../CreateRoomModal";

import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCircle,
  FaMapMarkerAlt
} from 'react-icons/fa';

import { 
  getTableHeaderStyle, 
  getFormSelectContainerStyle, 
  getFormSelectStyle, 
  getMultilineFieldStyle,
  DesignSystem 
} from '../../styles/designSystem';

interface ProjectLocationsProps {
  projectData: any;
  projectId: string;
}

interface LocationRowData {
  id: string;
  name: string;
  room_identifier?: string;
  site_id: string;
  floor_id: string;
  directions?: string;
  access_requirements?: string;
  access_contacts?: string; // Keep for backward compatibility
  access_contacts_name?: string;
  access_contacts_surname?: string;
  contact_phone?: string;
  contact_email?: string;
  site?: {
    id: string;
    name: string;
    address: string;
  };
  floor?: {
    id: string;
    name: string;
  };
  racks?: any[];
}

// Location row component
const LocationRow = ({ 
  location, 
  projectId, 
  onSubmit,
  isNew = false
}: { 
  location: LocationRowData, 
  projectId: string, 
  onSubmit: Function,
  isNew?: boolean
}) => {
  const [form, setForm] = useState({ ...location });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setForm({ ...location });
  }, [location]);

  const getStatusColor = (hasAccess: boolean) => {
    return hasAccess ? DesignSystem.colors.status.completed : DesignSystem.colors.status.notStarted;
  };

  // Function to save changes
  const saveChanges = async (updatedForm: any) => {
    setIsSaving(true);
    try {
      await onSubmit(updatedForm);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    
    // Auto-save for certain fields immediately
    if (['room_identifier', 'contact_phone', 'contact_email'].includes(field)) {
      const updatedForm = { ...form, [field]: value };
      saveChanges(updatedForm);
    }
  };

  // Function to handle text field blur
  const handleFieldBlur = (field: string, value: any) => {
    const updatedForm = { ...form, [field]: value };
    setForm(updatedForm);
    saveChanges(updatedForm);
  };

  const hasAccessInfo = !!(form.access_requirements || form.access_contacts || form.access_contacts_name || form.access_contacts_surname || form.contact_phone || form.contact_email);

  return (
    <div className={`group hover:bg-gray-50/50 transition-all duration-200 ${isNew ? 'bg-blue-50/30' : ''}`}>
      {/* Main Row */}
      <div className="flex items-start px-6 py-4 relative" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-gray-100 transition-colors relative z-10 flex items-center"
        >
          <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Access Status Indicator */}
        <div className="mr-4 relative z-10 flex items-start pt-1">
          <div className="relative">
            {/* Rotating Ring around status dot - when saving any field */}
            {isSaving && (
              <div className={`absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin ${
                hasAccessInfo ? 'border-t-green-500/60 border-r-green-500/60' : 'border-t-gray-300/60 border-r-gray-300/60'
              }`} style={{ animation: 'spin 3s linear infinite' }} />
            )}
            {/* Status Dot */}
            <FaCircle 
              className="w-2 h-2" 
              style={{ color: getStatusColor(hasAccessInfo) }}
            />
          </div>
        </div>

        {/* Room Name */}
        <div className="flex-1 min-w-0 relative z-10 flex items-start">
          <input
            type="text"
            value={form.name || ''}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            onBlur={(e) => handleFieldBlur('name', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none focus:ring-0 placeholder-gray-400 font-medium text-gray-900 text-base"
            placeholder="Room name..."
            style={{
              fontSize: '15px',
              fontFamily: '"Arcadia Text", system-ui, sans-serif',
              fontWeight: 360,
            }}
          />
        </div>

        {/* Site */}
        <div className="mx-6 w-32 relative z-10 flex items-start">
          <textarea
            value={form.site?.name || ''}
            onChange={() => {
              // Since site is read-only, we don't really change it
              // but we still need the onChange to avoid React warnings
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none placeholder-gray-400 resize-none"
            style={getMultilineFieldStyle()}
            placeholder="No site"
            rows={3}
            readOnly
          />
        </div>

        {/* Floor */}
        <div className="mx-6 w-24 relative z-10 flex items-start">
          <div className="text-sm text-gray-700">
            {form.floor?.name || 'No floor'}
          </div>
        </div>

        {/* Contact Info - Expanded width */}
        <div className="mx-6 w-40 relative z-10 flex items-start">
          <div className="text-sm text-gray-700">
            {(form.access_contacts_name || form.access_contacts_surname) && (
              <div className="truncate font-medium">
                {`${form.access_contacts_name || ''} ${form.access_contacts_surname || ''}`.trim()}
              </div>
            )}
            {form.contact_phone && (
              <div className="truncate text-xs">{form.contact_phone}</div>
            )}
            {form.contact_email && (
              <div className="truncate text-xs text-gray-500">{form.contact_email}</div>
            )}
            {!form.access_contacts_name && !form.access_contacts_surname && !form.contact_phone && !form.contact_email && (
              <span className="text-gray-400">No contact</span>
            )}
          </div>
        </div>

        {/* Racks Count */}
        <div className="mx-6 w-20 relative z-10 flex items-start">
          <div className="text-sm text-gray-700 text-center">
            {form.racks?.length || 0}
          </div>
        </div>

        {/* Address */}
        <div className="mx-6 w-48 relative z-10 flex items-start">
          <textarea
            value={form.site?.address || ''}
            onChange={() => {
              // Since address is read-only, we don't really change it
              // but we still need the onChange to avoid React warnings
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none placeholder-gray-400 resize-none"
            style={getMultilineFieldStyle()}
            placeholder="No address"
            rows={3}
            readOnly
          />
        </div>

        {/* Actions */}
        <div className="ml-6 w-20 relative z-10 flex items-start">
          <Link
            to={`/projects/${projectId}/sites/${form.site_id}/floors/${form.floor_id}/rooms/${form.id}`}
            className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            <span>Open</span>
          </Link>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 py-4 bg-gray-50/30" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
          <div className="grid grid-cols-12 gap-4">
            {/* Room ID */}
            <div className="col-span-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Room ID</label>
              <input
                type="text"
                value={form.room_identifier || ''}
                onChange={(e) => handleFieldChange('room_identifier', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Room ID..."
              />
            </div>

            {/* Access Requirements */}
            <div className="col-span-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Access Requirements</label>
              <textarea
                value={form.access_requirements || ''}
                onChange={(e) => setForm(prev => ({ ...prev, access_requirements: e.target.value }))}
                onBlur={(e) => handleFieldBlur('access_requirements', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Access requirements..."
              />
            </div>

            {/* Access Contacts */}
            <div className="col-span-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Access Contacts</label>
              
              {/* Name and Surname inputs */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  value={form.access_contacts_name || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, access_contacts_name: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('access_contacts_name', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="First name..."
                />
                <input
                  type="text"
                  value={form.access_contacts_surname || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, access_contacts_surname: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('access_contacts_surname', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Last name..."
                />
              </div>
              
              <div className="space-y-1">
                <input
                  type="tel"
                  value={form.contact_phone || ''}
                  onChange={(e) => handleFieldChange('contact_phone', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Phone number..."
                />
                <input
                  type="email"
                  value={form.contact_email || ''}
                  onChange={(e) => handleFieldChange('contact_email', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Email address..."
                />
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export const ProjectLocations = ({ projectData, projectId }: ProjectLocationsProps) => {
  const { keycloak: { token } } = useKeycloak();
  const [locations, setLocations] = useState<LocationRowData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [siteFilter, setSiteFilter] = useState('all');
  const [floorFilter, setFloorFilter] = useState('all');
  const [pageSize, setPageSize] = useState(25);
  
  // Modals
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);


  useEffect(() => {
    const handleScroll = () => {
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load locations from API instead of project data
  const loadLocations = async () => {
    if (!projectId || !token) return;
    
    setIsLoading(true);
    try {
      // Load full room data with racks from API
      const roomsData = await getRoomsByProjectId(token, projectId);
      console.log('ProjectLocations: Loaded rooms from API:', roomsData);
      
      // Create site address lookup from project data
      const siteAddressMap = new Map();
      if (projectData.sites) {
        projectData.sites.forEach((site: any) => {
          const siteId = site.site_id || site.id;
          const address = site.google_address_data?.formatted_address || site.address || '';
          siteAddressMap.set(siteId, address);
        });
      }
      
      // Create site and floor lookup maps
      const siteMap = new Map();
      const floorMap = new Map();
      
      if (projectData.sites) {
        // Load floors for each site to get proper floor names
        for (const site of projectData.sites) {
          const siteId = site.site_id || site.id;
          siteMap.set(siteId, site);
          
          try {
            // Load floors for this site to get accurate floor names
            console.log(`Loading floors for site ${siteId}...`);
            const floorsData = await getFloorsBySiteId(token, projectId, siteId);
            console.log(`Loaded ${floorsData.length} floors for site ${siteId}:`, floorsData);
            
            floorsData.forEach((floor: any) => {
              floorMap.set(floor.id, floor);
            });
          } catch (error) {
            console.warn(`Failed to load floors for site ${siteId}:`, error);
            // Fallback to floors from project data if available
            if (site.floors) {
              site.floors.forEach((floor: any) => {
                floorMap.set(floor.id, floor);
              });
            }
          }
        }
      }
      
      const transformedLocations: LocationRowData[] = roomsData.map((room: any) => {
        const site = siteMap.get(room.site_id);
        const floor = floorMap.get(room.floor_id);
        const siteAddress = siteAddressMap.get(room.site_id) || '';
        
        return {
          id: room.id || '',
          name: room.name,
          room_identifier: room.room_identifier || '',
          site_id: room.site_id,
          floor_id: room.floor_id,
          directions: room.directions || '',
          access_requirements: room.access_requirements || '',
          access_contacts: room.access_contacts || '', // Keep for backward compatibility
          access_contacts_name: room.access_contacts_name || '',
          access_contacts_surname: room.access_contacts_surname || '',
          contact_phone: room.contact_phone || '',
          contact_email: room.contact_email || '',
          site: {
            id: room.site_id,
            name: site?.name || 'Unknown Site',
            address: siteAddress
          },
          floor: {
            id: room.floor_id,
            name: floor?.name || 'Unknown Floor'
          },
          racks: room.rows?.flatMap((row: any) => row.racks || []) || []
        };
      });
      setLocations(transformedLocations);
    } catch (error) {
      console.error('Error loading locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLocations();
  }, [projectId, token]);

  // Listen for room creation events to refresh locations list
  useEffect(() => {
    const handleRoomCreated = () => {
      console.log('🏠 Room created event received in ProjectLocations, refreshing locations...');
      loadLocations();
    };

    window.addEventListener('roomCreated', handleRoomCreated);
    
    return () => {
      window.removeEventListener('roomCreated', handleRoomCreated);
    };
  }, []);

  // Filter locations based on search and filters
  const filteredLocations = useMemo(() => {
    return locations.filter(location => {
      // Search filter
      const matchesSearch = !searchQuery || 
        location.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.room_identifier?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.site?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.floor?.name?.toLowerCase().includes(searchQuery.toLowerCase());

      // Site filter
      const matchesSite = siteFilter === 'all' || location.site?.name === siteFilter;

      // Floor filter
      const matchesFloor = floorFilter === 'all' || location.floor?.name === floorFilter;

      return matchesSearch && matchesSite && matchesFloor;
    }).slice(0, pageSize);
  }, [locations, searchQuery, siteFilter, floorFilter, pageSize]);

  // Update location handler
  const updateLocationHandler = async (form: LocationRowData) => {
    if (!token || !projectId) return;

    try {
      // Create clean update data
      const updateData = {
        room_identifier: form.room_identifier,
        directions: form.directions,
        access_requirements: form.access_requirements,
        access_contacts: form.access_contacts, // Keep for backward compatibility
        access_contacts_name: form.access_contacts_name,
        access_contacts_surname: form.access_contacts_surname,
        contact_phone: form.contact_phone,
        contact_email: form.contact_email,
        project_id: projectId
      };

      await updateRoomAccess(token, projectId, form.id, updateData);
      setLocations(prev => prev.map(l => l.id === form.id ? { ...l, ...form } : l));
      toast.success('Location updated successfully');
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Failed to update location');
    }
  };



  const getAccessCount = () => {
    return locations.filter(l => !!(l.access_requirements || l.access_contacts || l.access_contacts_name || l.access_contacts_surname || l.contact_phone || l.contact_email)).length;
  };

  // Get unique sites and floors for filters
  const uniqueSites = useMemo(() => {
    const sites = locations.map(l => l.site?.name).filter(Boolean);
    return [...new Set(sites)] as string[];
  }, [locations]);

  const uniqueFloors = useMemo(() => {
    const floors = locations.map(l => l.floor?.name).filter(Boolean);
    return [...new Set(floors)] as string[];
  }, [locations]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className="bg-white rounded-lg transition-shadow duration-200" style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
        <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search locations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-80 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-500">
                      {filteredLocations.length} of {locations.length} locations
                    </span>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <span className="text-gray-500">
                      {getAccessCount()} with access info
                    </span>
          </div>
            </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Filters */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      showFilters 
                        ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    <FaFilter className="w-3.5 h-3.5" />
                    <span>Filter</span>
                    {(siteFilter !== 'all' || floorFilter !== 'all') && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                        {[siteFilter, floorFilter].filter(f => f !== 'all').length}
                      </span>
                    )}
                  </button>

                  {/* Add Location */}
            <button 
                    onClick={() => setShowAddLocationModal(true)}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
            >
                    <FaPlus className="w-3.5 h-3.5" />
                    <span>Add Location</span>
            </button>
          </div>
        </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #E5E7EB' }}>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Sites</option>
                        {uniqueSites.map((site) => (
                          <option key={site} value={site}>
                            {site}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={floorFilter}
                        onChange={(e) => setFloorFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Floors</option>
                        {uniqueFloors.map((floor) => (
                          <option key={floor} value={floor}>
                            {floor}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        style={getFormSelectStyle()}
                      >
                        <option value={25}>Show 25</option>
                        <option value={50}>Show 50</option>
                        <option value={100}>Show 100</option>
                        <option value={200}>Show All</option>
                      </select>
                    </div>
      </div>

                  {/* Active Filters & Clear */}
                  {(siteFilter !== 'all' || floorFilter !== 'all') && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Active filters:</span>
                        {siteFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                            Site: {siteFilter}
                            <button onClick={() => setSiteFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                          </span>
                        )}
                        {floorFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                            Floor: {floorFilter}
                            <button onClick={() => setFloorFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                            </span>
                          )}
                      </div>
                      <button
                        onClick={() => {
                          setSiteFilter('all');
                          setFloorFilter('all');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="w-8"></div> {/* Expand button space */}
                <div className="w-4 mr-4"></div> {/* Status indicator space */}
                <div className="flex-1" role="columnheader">Room Name</div>
                <div className="mx-6 w-32" role="columnheader">Site</div>
                <div className="mx-6 w-24" role="columnheader">Floor</div>
                <div className="mx-6 w-40" role="columnheader">Contact</div>
                <div className="mx-6 w-20" role="columnheader">Racks</div>
                <div className="mx-6 w-48" role="columnheader">Address</div>
                <div className="ml-6 w-20" role="columnheader">Actions</div>
              </div>
            </div>
          </div>
                  </div>
      </div>

      {/* Location List - Scrollable Content */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-gray-500">Loading locations...</span>
              </div>
            </div>
          )}

          {/* Location Rows */}
          {!isLoading && (
            <div>
              {filteredLocations.map((location) => (
                <LocationRow 
                  key={location.id} 
                  location={location} 
                  projectId={projectId}
                  onSubmit={updateLocationHandler}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && filteredLocations.length === 0 && (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <FaMapMarkerAlt className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2">No locations found</h3>
              <p className="text-gray-600 font-light mb-8">
                {searchQuery || siteFilter !== 'all' || floorFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding your first location.'
                }
          </p>
          <button 
                onClick={() => setShowAddLocationModal(true)}
                className="bg-white border border-gray-200 text-gray-600 px-6 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
                Add First Location
          </button>
                  </div>
          )}
        </div>
      </div>

      {/* Add New Location Modal */}
      {showAddLocationModal && (
        <CreateRoomModal 
          onClose={() => {
            setShowAddLocationModal(false);
            // Reload locations after creating a new one
            loadLocations();
          }}
          projectId={projectId}
          title="Add New Location"
        />
      )}
    </div>
  );
}; 